import React from "react";
import { FilterLayerType, getLocation, PlanningItem } from "./planningMapHelpers";
import { FilterProviderRef, FilterValues } from "components/filters/FilterContext";
import { useCustomQuery } from "utilities/hookutils";
import { AxiosPromise } from "axios";
import { appendFilterValuesToQueryString } from "components/filters/mappingUtils";

type Parameters<TEntity, TRequest, TInstruction extends { page: number }> = {
  filterLayer: FilterLayerType,
  visibleLayers: FilterLayerType[],
  filterSet: server.dto.FilterSets,
  api: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<TEntity[], TRequest>>,
  initialInstruction: TInstruction,
  mapFilterValuesToInstruction: (filterSet: server.dto.FilterSets, filter: FilterValues, page: number, perPage: number) => TInstruction,
  mapResponseToFilterValues: (response: TRequest, values: FilterValues) => Partial<FilterValues>,
  defaultFilters: TRequest
}

export const usePlanningMapEntity = <TEntity extends PlanningItem, TRequest, TInstruction extends { page: number }>(params: Parameters<TEntity, TRequest, TInstruction>) => {
  const [initialLoading, setInitialLoading] = React.useState(true);

  const { filterLayer, visibleLayers, filterSet, api, initialInstruction, mapFilterValuesToInstruction, mapResponseToFilterValues, defaultFilters } = params;

  const [instruction, setInstruction] = React.useState<TInstruction>({
    ...initialInstruction,
    page: 1,
    perPage: 5000,
    cache: filterSet
  });
  const filterRef = React.useRef<FilterProviderRef>(null);

  const hasLayer = () => visibleLayers.includes(filterLayer);

  const query = useCustomQuery({
    enabled: hasLayer(),
    queryFn: () => paginateAll(api, instruction)
      .then(response => {
        return {
          ...response,
          items: response.items.filter(item => getLocation(item) != null)
        }
      }),

    queryKey: [filterSet, "show", instruction],
    initialData: { items: [], request: undefined, error: undefined },
    onSuccess: (response) => {
      if (initialLoading) {
        filterRef.current.setDefaultValues((prev: FilterValues) => {
          return mapResponseToFilterValues(defaultFilters, prev);
        });
      }
      filterRef.current.updateValues((prev: FilterValues) => {
        return mapResponseToFilterValues(response.request, prev);
      });
      setInitialLoading(false);
    },
    cacheTime: 0,
  });

  const onFilterChange = (filter: FilterValues, filtersWithValues: Partial<FilterValues>) => {
    const newInstruction = mapFilterValuesToInstruction(filterSet, filter, 1, 5000);
    appendFilterValuesToQueryString(filtersWithValues);
    setInstruction(newInstruction);
  };

  return {
    query,
    onFilterChange,
    filterRef,
    initialLoading,
    setInitialLoading
  }
}

const paginateAll = async <TItem, TRequest>(
  fetch: (instruction: { page: number }) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<TItem[], TRequest>>,
  instruction: { page: number }
) => {

  let fetchedAll = false;
  let allItems: TItem[] = [];
  let lastRequest: TRequest;;
  let page = 1;
  while (!fetchedAll) {
    const items = await fetch({ ...instruction, page });

    if (items.data.errors.length > 0) {
      return {
        items: [] as TItem[],
        request: items.data.request,
        error: items.data.errors[0].message
      };
    }

    allItems = allItems.concat(items.data.data ?? []);
    lastRequest = items.data.request;
    fetchedAll = page >= items.data.pagination.pageCount;
    page++;
  }

  return {
    items: allItems,
    request: lastRequest,
    error: undefined
  };
};
